import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, Star, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate } from '../../utils/cabinI18n';
import { useMarkBadgeSeen } from '../../hooks/cabin/useMarkBadgeSeen';
import { useConfetti } from '../../hooks/cabin/useConfetti';
import { useSoundEffects } from '../../hooks/cabin/useSoundEffects';

const RARITY_CONFIG = {
  common: {
    glow: 'shadow-[0_0_20px_4px_rgba(88,204,2,0.4)]',
    bg: 'from-emerald-400 to-green-500',
    label_tr: 'Yaygın',
    label_en: 'Common',
    confettiCount: 60,
    particles: 'spread-50',
  },
  uncommon: {
    glow: 'shadow-[0_0_24px_6px_rgba(28,176,246,0.45)]',
    bg: 'from-sky-400 to-blue-500',
    label_tr: 'Nadir Değil',
    label_en: 'Uncommon',
    confettiCount: 80,
    particles: 'spread-60',
  },
  rare: {
    glow: 'shadow-[0_0_28px_8px_rgba(206,130,255,0.5)]',
    bg: 'from-purple-400 to-violet-600',
    label_tr: 'Nadir',
    label_en: 'Rare',
    confettiCount: 100,
    particles: 'spread-70',
  },
  epic: {
    glow: 'shadow-[0_0_36px_10px_rgba(255,75,75,0.55)]',
    bg: 'from-rose-400 to-red-600',
    label_tr: 'Epik',
    label_en: 'Epic',
    confettiCount: 130,
    particles: 'spread-80',
  },
  legendary: {
    glow: 'shadow-[0_0_48px_16px_rgba(255,200,0,0.7)]',
    bg: 'from-amber-400 to-yellow-500',
    label_tr: 'Efsanevi',
    label_en: 'Legendary',
    confettiCount: 180,
    particles: 'spread-90',
  },
};

function BadgeDisplay({ badge, t, lang, shouldReduce }) {
  const rarity = badge.rarity || 'common';
  const cfg = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const isLegendary = rarity === 'legendary';

  const name = lang === 'tr' ? badge.name_tr : (badge.name_en || badge.name_tr);
  const description = lang === 'tr' ? badge.description_tr : (badge.description_en || badge.description_tr);
  const rarityLabel = lang === 'tr' ? cfg.label_tr : cfg.label_en;

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Badge icon */}
      <motion.div
        initial={shouldReduce ? {} : { scale: 0, rotate: -20, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 10, stiffness: 180, delay: 0.1 }}
        className={`relative w-28 h-28 flex items-center justify-center rounded-3xl bg-gradient-to-br ${cfg.bg} ${cfg.glow}`}
        aria-hidden="true"
      >
        {/* Emoji or icon */}
        <span className="text-5xl select-none" role="img" aria-label={name}>
          {badge.emoji || '🏅'}
        </span>

        {/* Legendary pulse ring */}
        {isLegendary && !shouldReduce && (
          <motion.div
            className="absolute inset-0 rounded-3xl ring-2 ring-amber-300"
            animate={{ scale: [1, 1.08, 1], opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
      </motion.div>

      {/* Rarity pill */}
      <motion.span
        initial={shouldReduce ? {} : { opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        className={`px-3 py-1 rounded-full text-xs font-black text-white bg-gradient-to-r ${cfg.bg} uppercase tracking-wide`}
      >
        {rarityLabel}
      </motion.span>

      {/* Name */}
      <motion.div
        initial={shouldReduce ? {} : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="text-center"
      >
        <h3 className="text-xl font-black text-[var(--text-main)]">{name}</h3>
        <p className="text-sm text-[var(--text-muted)] mt-1 max-w-xs">{description}</p>
      </motion.div>

      {/* Rewards */}
      <motion.div
        initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex items-center gap-3"
      >
        {badge.reward_xp > 0 && (
          <span className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 border-2 border-amber-200 rounded-xl text-sm font-black text-amber-600">
            <Zap size={14} aria-hidden="true" />
            +{badge.reward_xp} XP
          </span>
        )}
        {badge.reward_miles > 0 && (
          <span className="flex items-center gap-1 px-3 py-1.5 bg-sky-50 border-2 border-sky-200 rounded-xl text-sm font-black text-sky-600">
            <Star size={14} aria-hidden="true" />
            +{badge.reward_miles} {t('cabin.gamification.badgeUnlock.miles_unit') || 'Mil'}
          </span>
        )}
      </motion.div>
    </div>
  );
}

/**
 * BadgeUnlockModal — shows queued badges one at a time.
 * Props:
 *  - badges: array of badge objects (from lesson complete response)
 *  - onClose: fn — called when all badges dismissed
 */
export default function BadgeUnlockModal({ badges = [], onClose }) {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const { mutate: markSeen } = useMarkBadgeSeen();
  const { fire } = useConfetti();
  const { play } = useSoundEffects();

  const currentBadge = badges[index];
  const isLastBadge = index >= badges.length - 1;

  const fireCelebration = useCallback(
    (badge) => {
      if (shouldReduce) return;
      const rarity = badge?.rarity || 'common';
      const cfg = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
      fire({
        particleCount: cfg.confettiCount,
        spread: 70,
        origin: { y: 0.5 },
      });
    },
    [shouldReduce, fire]
  );

  // Fire confetti + sound when modal opens or advances
  useEffect(() => {
    if (currentBadge) {
      fireCelebration(currentBadge);
      play('levelUp');
    }
  }, [index, currentBadge, fireCelebration, play]);

  const handleDismiss = () => {
    // Mark current badge as seen
    if (currentBadge?.slug) {
      markSeen(currentBadge.slug);
    }

    if (!isLastBadge) {
      setIndex((prev) => prev + 1);
    } else {
      onClose?.();
    }
  };

  if (!currentBadge) return null;

  const remainingCount = badges.length - index - 1;

  return (
    <AnimatePresence>
      <motion.div
        key="badge-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        role="dialog"
        aria-modal="true"
        aria-label={t('cabin.gamification.badgeUnlock.modal_aria') || 'Yeni rozet kazandın'}
        onClick={(e) => e.target === e.currentTarget && handleDismiss()}
      >
        <motion.div
          key={`badge-card-${index}`}
          initial={shouldReduce ? {} : { scale: 0.85, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={shouldReduce ? {} : { scale: 0.9, opacity: 0, y: -10 }}
          transition={{ type: 'spring', damping: 14, stiffness: 200 }}
          className="bg-white rounded-3xl border-2 border-gray-200 shadow-[0_8px_0_0_#E5E7EB] w-full max-w-sm p-8 relative flex flex-col items-center gap-6"
          data-testid="badge-unlock-modal"
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            aria-label={t('common.close') || 'Kapat'}
          >
            <X size={18} aria-hidden="true" />
          </button>

          {/* Queue indicator */}
          {badges.length > 1 && (
            <div className="absolute top-4 left-4 flex gap-1">
              {badges.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === index ? 'bg-[var(--brand-primary)]' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Header */}
          <motion.div
            initial={shouldReduce ? {} : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="text-xs font-black text-[var(--brand-primary)] uppercase tracking-widest">
              {t('cabin.gamification.badgeUnlock.new_badge') || 'Yeni Rozet!'}
            </p>
          </motion.div>

          {/* Badge display */}
          <BadgeDisplay
            badge={currentBadge}
            t={t}
            lang={lang}
            shouldReduce={shouldReduce}
          />

          {/* CTA */}
          <motion.button
            initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={shouldReduce ? {} : { scale: 1.02 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={handleDismiss}
            data-testid="badge-unlock-cta"
            className="w-full py-3.5 bg-[var(--brand-primary)] text-white font-black text-lg rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          >
            {remainingCount > 0
              ? interpolate(
                  t('cabin.gamification.badgeUnlock.next_badge') || 'Sonraki ({count})',
                  { count: remainingCount }
                )
              : (t('cabin.gamification.badgeUnlock.done') || 'Harika!')
            }
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
